import { Modal, Typography } from 'antd';
import React, {
  FC,
  useEffect,
  useState,
} from 'react';
import { ObservationSchema } from '@ynomia/core/dist/blueprint';
import { FormEntry } from '@ynomia/dynamic-form';
import { RequestError } from '@ynomia/client';
import {
  analytics,
  atLeast,
  fetchAutofillObservations,
  submitObservation,
} from '../../../../../services';
import ModalForm from '../../../../atoms/ModalForm';
import { getContextStores } from '../../../../../context';
import {
  getAssetTypes,
  getScratchProjectCode,
  getTenant,
} from '../../../../../selectors';
import { RawObservationRecord } from '../../../../../config/types';
import { getAssets } from '../../../../../actions';
import { notification } from '../../../../../antdProvider';
import { SkeletonLoader } from '../../../../atoms';

interface Props {
  assetId: string,
  observation: ObservationSchema,
}

const AssetObservation: FC<Props> = ({
  assetId,
  observation,
}) => {
  /* Context  */
  const contextStores = getContextStores();
  const { assetState, assetDispatch } = contextStores;
  const { assetsKeyedById } = assetState;
  const tenant = getTenant(contextStores);
  const projectCode = getScratchProjectCode(contextStores);
  const assetTypes = getAssetTypes(contextStores);
  const { lastFetchStartTime } = assetState;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFetchingAutofillObservations, setIsFetchingAutofillObservations] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [autofillSkipped, setAutofillSkipped] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});

  const showModal = () => {
    setIsModalOpen(true);
    analytics.trackEvent('Edit Observation Opened', { observation_label: observation.label });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    analytics.trackEvent('Edit Observation Closed', { observation_label: observation.label });
  };

  const skipAutofill = () => {
    setAutofillSkipped(true);
    setIsFetchingAutofillObservations(false);
  };

  const fields: Array<FormEntry> = observation.fields
    ?.filter(field => field.readonly !== true && field.hidden !== true)
    ?.map(field => ({
      entryComponent: field.fieldType,
      id: field.id,
      label: field.label,
      isRequired: field.required,
      show: field.show,
      disabled: field.disabled || isFetchingAutofillObservations,
      properties: field.properties,
      validationConditions: field.validationConditions,
    }));

  const getAutofillObservations = async (scope: 'element' | 'observation' = 'element') => {
    setIsFetchingAutofillObservations(true);

    try {
      const promise = fetchAutofillObservations(
        scope === 'element' ? assetId : undefined,
        tenant,
        projectCode,
        observation.id,
      );

      const response = await atLeast(promise, 100);
      const observations = response as unknown as Array<RawObservationRecord>;

      if (autofillSkipped) return;
      const values = observations[0]?.fields;
      setDefaultValues(values || {});
    } catch {
      skipAutofill();
    } finally {
      setIsFetchingAutofillObservations(false);
    }
  };

  const submitHandler = async (formData: { [key: string]: any }) => {
    setSubmitLoading(true);

    try {
      await submitObservation(
        tenant,
        projectCode,
        observation,
        assetId,
        formData,
      );
      await getAssets(tenant, projectCode, assetTypes, lastFetchStartTime, assetDispatch);
    } catch (error) {
      const { status, body } = error as RequestError;
      notification.error({
        message: status,
        description: body?.msg || '',
      });
    }
    setSubmitLoading(false);
    setIsModalOpen(false);
  };

  const asset = assetsKeyedById.get(assetId);

  useEffect(() => {
    const shouldAutoFill = (observation.singleton && asset?.eventMetrics?.[observation.id]?.count)
      || observation.autofill?.enabled;
    if (isModalOpen && shouldAutoFill) {
      getAutofillObservations(observation.autofill?.scope);
    }
  }, [isModalOpen]);

  return (
    <>
      <Typography.Link onClick={showModal}>
        {`${observation.actionLabel || 'Open'} ${observation.label}`}
      </Typography.Link>
      <Modal
        title={`${observation.label}`}
        open={isModalOpen}
        closable={false}
        footer={[]}
        style={{ padding: 0 }}
        destroyOnClose
      >
        {(isFetchingAutofillObservations)
          ? <SkeletonLoader />
          : (
            <ModalForm
              defaultValues={defaultValues}
              fields={fields || []}
              filtrexProps={{
                element: asset,
              }}
              onCancel={handleCancel}
              onSubmit={submitHandler}
              submitButtonLoading={submitLoading}
            />
          )}
      </Modal>
    </>
  );
};

export default AssetObservation;
